import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import {
  LuAlertTriangle,
  LuCheck,
  LuChevronLeft,
  LuImagePlus,
  LuLoader2,
  LuRefreshCw,
  LuSlidersHorizontal,
  LuTrash2,
  LuUpload,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../../../components/graphics/LottieLoading";
import {
  useAddBillingLogoMutation,
  useDeleteBillingLogoMutation,
  useGetBillingLogoQuery,
  useUpdateBillingLogoMutation,
} from "../../../../services/BillingService";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  BILLING_CARD,
  BILLING_PRIMARY_BUTTON,
  BILLING_SECONDARY_BUTTON,
} from "../../../billing/ui/BillingUi";
import { InvoiceLogoPreview } from "../logo/InvoiceLogoPreview";
import {
  ACCEPT_ATTRIBUTE,
  alignPlacement,
  currentAlign,
  initialPlacement,
  logoFileProblem,
  PDF_LOGO_BOX_MM,
  pdfLogoSize,
  Placement,
  savedLogoQuality,
} from "../logo/logo.utils";
import {
  fetchLogoAsFile,
  LoadedLogo,
  loadLogoFile,
  renderLogoPng,
} from "../logo/logoCanvas";
import { cropOf, LogoEditor, QualityChip } from "../logo/LogoEditor";
import { CHECKERBOARD, FramedPicture } from "../logo/LogoFrame";

// Settings > Billing > Logo (Roadmap 1847): pick a file, frame it, see it on
// an invoice, save. The saved logo is always a 1200 x 400 PNG that fills the
// invoice's logo space (60 x 20 mm), sent through the existing firm-logo API.

type Props = {
  onClose?: () => void;
};

type Draft = { logo: LoadedLogo; trimOn: boolean; placement: Placement };

const errorText = (error: unknown) => {
  const data = (error as { data?: unknown })?.data;
  if (
    typeof data === "string" &&
    data.length < 200 &&
    !data.includes("Exception") &&
    // The server's generic 5xx sentence (since 2026-10-22): the logo text says more.
    data !== "Something went wrong on our side."
  ) {
    return data;
  }
  return "The logo couldn't be saved. Please try again.";
};

const Card: FC<{
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, right, children }) => (
  <section className={classNames(BILLING_CARD, "p-5 sm:p-6")}>
    <div className={"mb-4 flex flex-wrap items-start justify-between gap-3"}>
      <div>
        <h2 className={"text-[15px] font-bold text-[#1F2433]"}>{title}</h2>
        {subtitle && (
          <p className={"mt-0.5 text-[13px] text-[#6B7280]"}>{subtitle}</p>
        )}
      </div>
      {right}
    </div>
    {children}
  </section>
);

const TIPS: { title: string; text: string }[] = [
  {
    title: "File types",
    text: "PNG, JPG, SVG or WebP, up to 10 MB.",
  },
  {
    title: "Best shape",
    text: "A wide (horizontal) logo. The logo space is 3 times wider than tall: about 2.4 × 0.8 in (60 × 20 mm), at the top left of the invoice.",
  },
  {
    title: "Best size",
    text: "At least 600 px wide (1200 px is perfect). Smaller pictures can look blurry when printed. SVG is always sharp.",
  },
  {
    title: "Background",
    text: "A PNG with a transparent background looks cleanest. A white background is fine too: the invoice is white.",
  },
  {
    title: "No resizing needed",
    text: "Empty space around the logo is cut off for you, and the logo is saved at the right size automatically.",
  },
];

export const BillingLogo: FC<Props> = () => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: saved, isLoading, isFetching } = useGetBillingLogoQuery(orgId);
  const [addLogo] = useAddBillingLogoMutation();
  const [updateLogo] = useUpdateBillingLogoMutation();
  const [deleteLogo, { isLoading: isRemoving }] =
    useDeleteBillingLogoMutation();

  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [opening, setOpening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [savedSize, setSavedSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const savedUrl = saved?.imageURI || "";

  useEffect(() => {
    setSavedSize(null);
  }, [savedUrl]);

  // Free the picture's memory when it is replaced or the page closes.
  const draftUrl = draft?.logo.url;
  useEffect(
    () => () => {
      if (draftUrl) URL.revokeObjectURL(draftUrl);
    },
    [draftUrl],
  );

  const openFile = async (file: File | undefined | null, savedLogo = false) => {
    if (!file) return;
    setProblem("");
    const fileProblem = logoFileProblem(file);
    if (fileProblem) {
      setProblem(fileProblem);
      return;
    }
    setOpening(true);
    try {
      const logo = await loadLogoFile(file);
      // A logo saved by this page (3:1) opens exactly as it is now.
      const asSaved =
        savedLogo && Math.abs(logo.width / logo.height - 3) < 0.01;
      const trimOn = !!logo.trim && !asSaved;
      setDraft({
        logo,
        trimOn,
        placement: initialPlacement(cropOf(logo, trimOn)),
      });
      setConfirmRemove(false);
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setOpening(false);
    }
  };

  const chooseFile = () => inputRef.current?.click();

  const adjustSaved = async () => {
    setProblem("");
    setOpening(true);
    const file = savedUrl ? await fetchLogoAsFile(savedUrl) : null;
    setOpening(false);
    if (!file) {
      setProblem(
        "The saved logo couldn't be opened for adjusting. Please upload your logo file again.",
      );
      return;
    }
    openFile(file, true);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setProblem("");
    try {
      const crop = cropOf(draft.logo, draft.trimOn);
      const png = await renderLogoPng(draft.logo, crop, draft.placement);
      const formData = new FormData();
      formData.append("Name", "logo.png");
      formData.append(
        "Image",
        new File([png.blob], "logo.png", { type: "image/png" }),
      );
      formData.append("ImageType", savedUrl ? ".png" : "image/png");
      formData.append("OrgId", orgId);
      if (savedUrl) await updateLogo(formData).unwrap();
      else await addLogo(formData).unwrap();
      setDraft(null);
      toast.success("Logo saved. It will be on your invoices from now on.");
    } catch (e) {
      const message = errorText(e);
      setProblem(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await deleteLogo(orgId).unwrap();
      setConfirmRemove(false);
      toast.success("Logo removed. Invoices will have no logo.");
    } catch {
      toast.error("The logo couldn't be removed. Please try again.");
    }
  };

  const onDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    openFile(e.dataTransfer?.files?.[0]);
  };
  const dropProps = {
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      setDragOver(true);
    },
    onDragLeave: () => setDragOver(false),
    onDrop,
  };

  if (isLoading) return <LottieLoading />;

  const crop = draft ? cropOf(draft.logo, draft.trimOn) : null;
  const savedPdf = savedSize
    ? pdfLogoSize(savedSize.width, savedSize.height)
    : null;
  const savedQuality = savedSize
    ? savedLogoQuality(savedSize.width, savedSize.height)
    : null;

  const savedLogoImage = (measure: boolean) =>
    savedUrl ? (
      <img
        src={savedUrl}
        alt={measure ? "Your logo" : ""}
        onLoad={
          measure
            ? (e) =>
                setSavedSize({
                  width: e.currentTarget.naturalWidth,
                  height: e.currentTarget.naturalHeight,
                })
            : undefined
        }
        className={"absolute left-0 top-0 max-h-none max-w-none"}
        style={
          savedPdf
            ? {
                width: `${(savedPdf.width / PDF_LOGO_BOX_MM.width) * 100}%`,
                height: `${(savedPdf.height / PDF_LOGO_BOX_MM.height) * 100}%`,
              }
            : { visibility: "hidden" }
        }
      />
    ) : null;

  const draftInSpace =
    draft && crop ? (
      <div className={"absolute inset-0"}>
        <FramedPicture
          url={draft.logo.url}
          width={draft.logo.width}
          height={draft.logo.height}
          crop={crop}
          placement={draft.placement}
        />
      </div>
    ) : null;

  const busy = saving || isRemoving || isFetching;

  return (
    <div className={"pb-10 font-Manrope text-[#1F2433]"}>
      <input
        ref={inputRef}
        type={"file"}
        accept={ACCEPT_ATTRIBUTE}
        className={"hidden"}
        data-testid={"logo-file-input"}
        onChange={(e) => {
          const input = e.currentTarget;
          openFile(input.files?.[0]);
          // Lets the same file be picked again after Cancel.
          input.value = "";
        }}
      />

      <div className={"my-6 flex items-start gap-3"}>
        <button
          type={"button"}
          aria-label={"Back"}
          onClick={() => navigate(-1)}
          className={
            "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[#DFE2E8] bg-white text-[#3A4256] hover:bg-[#F7F8FA]"
          }>
          <LuChevronLeft className={"text-lg"} />
        </button>
        <div>
          <h1 className={"text-2xl font-bold"}>Invoice logo</h1>
          <p className={"mt-1 text-[13.5px] text-[#6B7280]"}>
            Your logo prints at the top left of every invoice PDF, including the
            invoices your clients download.
          </p>
        </div>
      </div>

      <div className={"grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]"}>
        <div className={"flex min-w-0 flex-col gap-5"}>
          {draft ? (
            <Card
              title={"Adjust your logo"}
              subtitle={`${draft.logo.fileName} · move and size it inside the logo space, then save.`}
              right={
                <button
                  type={"button"}
                  className={classNames(
                    BILLING_SECONDARY_BUTTON,
                    "!h-9 !px-3.5 !text-[13px]",
                  )}
                  onClick={chooseFile}
                  disabled={saving}>
                  <LuRefreshCw /> Choose another file
                </button>
              }>
              <div {...dropProps}>
                <LogoEditor
                  logo={draft.logo}
                  trimOn={draft.trimOn}
                  placement={draft.placement}
                  onTrimChange={(trimOn) => {
                    const nextCrop = cropOf(draft.logo, trimOn);
                    const align = crop
                      ? currentAlign(crop, draft.placement)
                      : null;
                    setDraft({
                      ...draft,
                      trimOn,
                      placement: alignPlacement(
                        nextCrop,
                        initialPlacement(nextCrop),
                        align || "left",
                      ),
                    });
                  }}
                  onPlacementChange={(placement) =>
                    setDraft({ ...draft, placement })
                  }
                />
              </div>
              {problem && <Problem text={problem} />}
              <div
                className={
                  "mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-[#EDEFF3] pt-4"
                }>
                <button
                  type={"button"}
                  className={BILLING_SECONDARY_BUTTON}
                  disabled={saving}
                  onClick={() => {
                    setDraft(null);
                    setProblem("");
                  }}>
                  Cancel
                </button>
                <button
                  type={"button"}
                  className={BILLING_PRIMARY_BUTTON}
                  disabled={saving}
                  onClick={save}>
                  {saving ? (
                    <LuLoader2 className={"animate-spin"} />
                  ) : (
                    <LuCheck />
                  )}
                  {saving ? "Saving…" : "Save logo"}
                </button>
              </div>
            </Card>
          ) : savedUrl ? (
            <Card
              title={"Your logo"}
              subtitle={
                "This is exactly how it sits in the logo space on your invoices."
              }
              right={savedQuality && <QualityChip {...savedQuality} />}>
              <div
                {...dropProps}
                className={classNames(
                  "outline relative aspect-[3/1] w-full max-w-[600px] overflow-hidden rounded-xl outline-1 outline-offset-2",
                  dragOver ? "outline-[#6B4EFF]" : "outline-[#DCE2EA]",
                )}
                style={CHECKERBOARD}>
                {savedLogoImage(true)}
                {busy && (
                  <div
                    className={
                      "absolute inset-0 flex items-center justify-center bg-white/70"
                    }>
                    <LuLoader2
                      className={"animate-spin text-2xl text-[#6B4EFF]"}
                    />
                  </div>
                )}
              </div>
              {savedSize && (
                <p className={"mt-2 text-xs text-[#6B7280]"}>
                  {savedSize.width} × {savedSize.height} px · prints at{" "}
                  {Math.round(savedPdf?.width || 0)} ×{" "}
                  {Math.round(savedPdf?.height || 0)} mm
                  {savedQuality?.level === "blurry" &&
                    ". This logo is small, so it may look blurry on printed invoices: upload a bigger file with Replace."}
                </p>
              )}
              {problem && <Problem text={problem} />}
              <div className={"mt-5 flex flex-wrap items-center gap-3"}>
                <button
                  type={"button"}
                  className={BILLING_PRIMARY_BUTTON}
                  onClick={chooseFile}
                  disabled={busy || opening}>
                  {opening ? (
                    <LuLoader2 className={"animate-spin"} />
                  ) : (
                    <LuUpload />
                  )}
                  Replace logo
                </button>
                <button
                  type={"button"}
                  className={BILLING_SECONDARY_BUTTON}
                  onClick={adjustSaved}
                  disabled={busy || opening}>
                  <LuSlidersHorizontal /> Adjust size and position
                </button>
                {confirmRemove ? (
                  <div
                    className={
                      "flex flex-wrap items-center gap-2 rounded-[10px] bg-[#FEF2F2] px-3 py-1.5 text-[13px] text-[#991B1B]"
                    }>
                    Remove the logo from all invoices?
                    <button
                      type={"button"}
                      className={
                        "rounded-lg bg-[#DC2626] px-3 py-1 text-xs font-bold text-white hover:bg-[#B91C1C] disabled:opacity-60"
                      }
                      disabled={isRemoving}
                      onClick={remove}>
                      Remove
                    </button>
                    <button
                      type={"button"}
                      className={
                        "px-2 py-1 text-xs font-semibold text-[#3A4256]"
                      }
                      onClick={() => setConfirmRemove(false)}>
                      Keep it
                    </button>
                  </div>
                ) : (
                  <button
                    type={"button"}
                    className={classNames(
                      BILLING_SECONDARY_BUTTON,
                      "!text-[#DC2626] hover:!bg-[#FEF2F2]",
                    )}
                    onClick={() => setConfirmRemove(true)}
                    disabled={busy}>
                    <LuTrash2 /> Remove
                  </button>
                )}
              </div>
            </Card>
          ) : (
            <Card
              title={"Add your logo"}
              subtitle={"Upload it once and it appears on every invoice."}>
              <div
                {...dropProps}
                role={"button"}
                tabIndex={0}
                onClick={chooseFile}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    chooseFile();
                  }
                }}
                className={classNames(
                  "flex min-h-[240px] cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors",
                  dragOver
                    ? "border-[#6B4EFF] bg-[#F4F1FF]"
                    : "border-[#DCE2EA] bg-[#FAFBFC] hover:border-[#B9A9FF] hover:bg-[#F8F6FF]",
                )}
                data-testid={"logo-dropzone"}>
                <div
                  className={
                    "flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EFEBFF] text-[26px] text-[#6B4EFF]"
                  }>
                  {opening ? (
                    <LuLoader2 className={"animate-spin"} />
                  ) : (
                    <LuImagePlus />
                  )}
                </div>
                <div className={"text-[15px] font-bold"}>
                  Drag your logo here
                </div>
                <div className={"text-[13px] text-[#6B7280]"}>
                  or{" "}
                  <span
                    className={
                      "font-bold text-[#6B4EFF] underline-offset-2 hover:underline"
                    }>
                    choose a file
                  </span>{" "}
                  · PNG, JPG, SVG or WebP, up to 10 MB
                </div>
              </div>
              {problem && <Problem text={problem} />}
            </Card>
          )}

          <Card
            title={"On your invoice"}
            subtitle={
              draft
                ? "Preview of the top of an invoice with the logo you're adjusting (not saved yet)."
                : "Preview of the top of an invoice, drawn at the real proportions."
            }>
            <InvoiceLogoPreview
              logo={draft ? draftInSpace : savedLogoImage(false)}
              showLogoSpace={!!draft || !savedUrl}
            />
            {!draft && !savedUrl && (
              <p className={"mt-3 text-xs text-[#6B7280]"}>
                The dashed box is where your logo will go.
              </p>
            )}
          </Card>
        </div>

        <aside className={"flex flex-col gap-5"}>
          <Card title={"What works best"}>
            <ul className={"flex flex-col gap-3.5"}>
              {TIPS.map((tip) => (
                <li key={tip.title} className={"flex gap-3"}>
                  <span
                    className={
                      "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#EFEBFF] text-[11px] text-[#6B4EFF]"
                    }>
                    <LuCheck />
                  </span>
                  <div>
                    <div className={"text-[13px] font-bold"}>{tip.title}</div>
                    <div className={"text-[13px] leading-5 text-[#6B7280]"}>
                      {tip.text}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </aside>
      </div>
    </div>
  );
};

const Problem: FC<{ text: string }> = ({ text }) => (
  <div
    role={"alert"}
    className={
      "mt-4 flex gap-2 rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-3.5 py-2.5 text-[13px] leading-5 text-[#991B1B]"
    }>
    <LuAlertTriangle className={"mt-0.5 shrink-0"} />
    <span>{text}</span>
  </div>
);
